import React, { useState } from 'react';
import ActionMenu from './ActionMenu';
import Pagination from './Pagination';

const DataTable = ({ 
  columns, 
  data, 
  onRowAction,
  onAction,
  actionMenuItems,
  renderCell,
  rowsPerPage = 10,
  showPagination = true,
  currentPage: externalCurrentPage,
  onPageChange: externalOnPageChange,
  pagination: externalPagination, // Support for server-side pagination
  totalRows, // Legacy support
  loading,
  containerClassName = "bg-white rounded-xl shadow-sm border border-gray-200 relative z-0 h-[70vh] ",
  tableWrapperClassName = "overflow-x-auto overflow-visible h-full",
  headerRowClassName = ""
}) => {
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);

  const currentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
  const setCurrentPage = externalOnPageChange || setInternalCurrentPage;

  // Pagination logic - support both client-side and server-side pagination
  const isServerSidePagination = externalPagination && (externalPagination.total !== undefined || externalPagination.page !== undefined);
  
  // Calculate total pages
  let totalPages, totalItems, itemsPerPage, serverCurrentPage;
  if (isServerSidePagination) {
    totalItems = externalPagination.total || 0;
    itemsPerPage = externalPagination.limit || rowsPerPage;
    totalPages = externalPagination.pages || Math.ceil(totalItems / itemsPerPage);
    serverCurrentPage = externalPagination.page || currentPage;
  } else {
    totalItems = totalRows !== undefined ? totalRows : data.length;
    itemsPerPage = rowsPerPage;
    totalPages = Math.ceil(totalItems / rowsPerPage);
    serverCurrentPage = currentPage;
  }
  
  const currentData = isServerSidePagination 
    ? data // Server-side: data is already paginated
    : data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage); // Client-side: slice the data

  return (
    <div className={containerClassName}>
      {/* Table */}
      <div className={tableWrapperClassName}>
        <table className="min-w-full">
          <thead>
            <tr className={`border-b border-gray-200 ${headerRowClassName}`}>
              {actionMenuItems && (
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-6 py-3 ${
                    column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'
                  } ${column.headerClassName || 'text-xs font-medium text-gray-500 uppercase tracking-wider'}`}
                >
                  {column.renderHeader ? column.renderHeader(column) : (column.label || column.header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (actionMenuItems ? 1 : 0)} className="px-6 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actionMenuItems ? 1 : 0)} className="px-6 py-8 text-center text-gray-500">
                  No data available
                </td>
              </tr>
            ) : (
              currentData.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                className={`${rowIndex !== currentData.length - 1 ? 'border-b border-gray-200' : ''} hover:bg-gray-50 transition-colors`}
              >
                {actionMenuItems && (
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <ActionMenu
                      items={typeof actionMenuItems === 'function' ? actionMenuItems(row) : actionMenuItems}
                      onAction={(action) => (onAction || onRowAction)?.(action, row)}
                      itemId={row.id || row._id}
                    />
                  </td>
                )}
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`px-6 py-4 ${column.nowrap ? 'whitespace-nowrap' : ''} ${
                      column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {column.render ? (
                      column.render(row[column.key || column.accessor], row)
                    ) : renderCell ? (
                      renderCell(column.accessor, row, column)
                    ) : (
                      <span className={`text-sm ${column.className || 'text-gray-900'}`}>
                        {row[column.accessor || column.key]}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <Pagination
          currentPage={serverCurrentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          showInfo={true}
        />
      )}
    </div>
  );
};

export default DataTable;

